import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams } from 'react-router-dom';
import { FooterNavContext } from './components/FooterNav';
import ScrollToTop from './components/ScrollToTop';
import AccessGuard from './components/AccessGuard';
// import FooterNav from './components/FooterNav';
import { message } from 'antd';
import { fetchGameInfoByGameID } from './api/gameAPI';
import { logoutUser } from './api/userAPI';
import { PAGE_ACCESS_RULES, toAccessGuardProps } from './config/pageAccessRules';
import {
  buildClientCompatibilityMessage,
  CLIENT_COMPATIBILITY_MESSAGE_DURATION_SECONDS,
  CLIENT_COMPATIBILITY_MESSAGE_KEY,
  getClientCompatibility,
  shouldShowClientCompatibilityReminder,
} from './utils/clientCompatibility';
const UserPage = lazy(() => import('./pages/UserPage'));
const HomePage = lazy(() => import('./pages/HomePage'));
const loadMatchesPage = () => import('./pages/MatchesPage');
const MatchesPage = lazy(loadMatchesPage);
const MatchesListPage = lazy(() => import('./pages/MatchesListPage'));
const loadMatchDetailsPage = () => import('./pages/MatchDetailsPage');
const MatchDetailsPage = lazy(loadMatchDetailsPage);
const ScheduleMatchPage = lazy(() => import('./pages/ScheduleMatchPage'));
const RecordGamePage = lazy(() => import('./pages/RecordGamePage'));
const ManageUserPage = lazy(() => import('./pages/ManageUserPage'));
const ManageBansPage = lazy(() => import('./pages/ManageBansPage'));
const PointsManagePage = lazy(() => import('./pages/PointsManagePage'));
const ManagePlayersPage = lazy(() => import('./pages/ManagePlayersPage'));
const DrawPage = lazy(() => import('./pages/DrawPage'));
const KnockoutPage = lazy(() => import('./pages/KnockoutPage'));
const GroupOverviewPage = lazy(() => import('./pages/GroupOverviewPage'));
const NowPlayingPageV2 = lazy(() => import('./pages/NowPlayingPageV2'));
const NowPlayingPageForOne = lazy(() => import('./pages/NowPlayingPageForOne'));
const NowPlayingPageForStreaming = lazy(() => import('./pages/NowPlayingPageForStreaming'));
const LiveAdjustHub = lazy(() => import('./pages/LiveAdjustHub'));
const LiveReorderPage = lazy(() => import('./pages/LiveReorderPage'));
const DrawDistributionPage = lazy(() => import('./pages/DrawDistributionPage'));
const DrawAnySourcePage = lazy(() => import('./pages/DrawAnySourcePage'));

const prefetchedPageKeys = new Set();

function canPrefetchByNetwork() {
  if (typeof navigator === 'undefined') return true;
  const connection =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection;
  if (!connection) return true;
  if (connection.saveData) return false;
  const effectiveType = String(connection.effectiveType || '').toLowerCase();
  return !effectiveType.includes('2g');
}

function prefetchPageOnce(key, loader) {
  if (prefetchedPageKeys.has(key)) return;
  prefetchedPageKeys.add(key);
  loader().catch(() => {
    prefetchedPageKeys.delete(key);
  });
}

function RouteLoadingFallback() {
  return (
    <div style={{ padding: '16px', textAlign: 'center' }}>
      页面加载中...
    </div>
  );
}

function RecordRouteGuard({ children }) {
  const { id } = useParams();
  const [resolvedMatchID, setResolvedMatchID] = useState(null);
  const [resolving, setResolving] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setResolving(true);
      try {
        const gameID = Number(id);
        if (!Number.isInteger(gameID) || gameID <= 0) {
          setResolvedMatchID(null);
          return;
        }
        const result = await fetchGameInfoByGameID(gameID);
        if (cancelled) return;
        const merged = {
          ...(result?.gameResults || {}),
          ...(result?.scheduleResults || {}),
          ...(result?.matchResults || {}),
        };
        const matchID = Number(merged?.matchID);
        if (Number.isInteger(matchID) && matchID > 0) {
          setResolvedMatchID(matchID);
        } else {
          setResolvedMatchID(null);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('解析 Record 页面 matchID 失败:', error);
        setResolvedMatchID(null);
      } finally {
        if (!cancelled) {
          setResolving(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  if (resolving) {
    return <RouteLoadingFallback />;
  }

  return (
    <AccessGuard
      matchID={resolvedMatchID}
      {...toAccessGuardProps(PAGE_ACCESS_RULES.recordPage)}
    >
      {children}
    </AccessGuard>
  );
}


function App() {

    useEffect(() => {
      const compatibility = getClientCompatibility();
      if (
        !compatibility.supported &&
        shouldShowClientCompatibilityReminder(compatibility, {
          appVersion: process.env.REACT_APP_VERSION,
        })
      ) {
        message.error({
          content: buildClientCompatibilityMessage(compatibility),
          duration: CLIENT_COMPATIBILITY_MESSAGE_DURATION_SECONDS,
          key: CLIENT_COMPATIBILITY_MESSAGE_KEY,
        });
      }
    }, []);

    /* --- 登录过期检测，只在应用首次挂载时跑一次 --- */
    useEffect(() => {
      const expiry = localStorage.getItem('loginExpiry');
      const hasLocalUsername = Boolean(localStorage.getItem('username'));
      if (!hasLocalUsername) {
        // 本地视为“未登录”时，顺便清理可能残留的后端 Cookie，避免权限状态不一致
        (async () => {
          try {
            await logoutUser();
          } catch (_) {}
        })();
        message.info('登录可查看更多信息');
        return;
      }
      if (!expiry || Date.now() > Number(expiry)) {
        (async () => {
          try {
            await logoutUser();
          } catch (error) {
            console.warn('登录过期时清理后端登录态失败:', error);
          } finally {
            // 已过期：清空所有与登录相关的信息
            localStorage.removeItem('username');
            localStorage.removeItem('userID');
            localStorage.removeItem('loginExpiry');
            localStorage.removeItem('currentDate');
            window.location.href = '/home'; // 跳转到主页
          }
        })();
        // window.location.reload();
        // 这里可以选择：
        // 1) 直接弹出登录 Modal（通过全局 context 控制）
        // 2) 或导航到 /login
        // 3) 或保持静默，等用户点“个人”时再提示
      }
    }, []);   // 依赖空数组 ⇒ 只跑一次

    useEffect(() => {
      if (typeof window === 'undefined') return undefined;
      if (!canPrefetchByNetwork()) return undefined;

      let idleId = null;
      let timerId = null;

      const runPrefetch = () => {
        prefetchPageOnce('matches', loadMatchesPage);
        prefetchPageOnce('match-details', loadMatchDetailsPage);
      };

      if ('requestIdleCallback' in window) {
        idleId = window.requestIdleCallback(runPrefetch, { timeout: 1200 });
      } else {
        timerId = window.setTimeout(runPrefetch, 300);
      }

      return () => {
        if ('cancelIdleCallback' in window && idleId != null) {
          window.cancelIdleCallback(idleId);
        }
        if (timerId != null) {
          window.clearTimeout(timerId);
        }
      };
    }, []);

    useEffect(() => {
      // 根据当前屏幕方向隐藏/显示微信底部工具栏
      function setToolbar() {
        if (window.innerWidth > window.innerHeight) {
            window.WeixinJSBridge.call('hideToolbar');
        } else {
            window.WeixinJSBridge.call('showToolbar');
        }
      }
      // 等微信桥接就绪后执行
      function onBridgeReady() {
        setToolbar();
        // 监听设备旋转或尺寸变化
        window.addEventListener('resize', setToolbar);
      }
  
      // 如果桥接还没注入，先绑定 ready 事件
      if (typeof window.WeixinJSBridge === 'undefined') {
        document.addEventListener('WeixinJSBridgeReady', onBridgeReady, false);
      } else {
        onBridgeReady();
      }
  
      // 卸载时清理事件监听
      return () => {
        window.removeEventListener('resize', setToolbar);
        document.removeEventListener('WeixinJSBridgeReady', onBridgeReady);
      };
    }, []);

    const [loginModalVisible, setLoginModalVisible] = useState(false);

    return (
        <FooterNavContext.Provider value={{ isLoginModalVisible: loginModalVisible, setLoginModalVisible: setLoginModalVisible }}>
            <Router>
                <ScrollToTop />
                <Suspense fallback={<RouteLoadingFallback />}>
                    <Routes>
                        <Route path={PAGE_ACCESS_RULES.homePage.path} element={<HomePage />} />
                        <Route path={PAGE_ACCESS_RULES.userPage.path} element={<UserPage />} />
                        <Route path={PAGE_ACCESS_RULES.matchesListPage.path} element={<MatchesListPage />} />
                        <Route path={PAGE_ACCESS_RULES.matchesPage.path} element={<MatchesPage />} />
                        <Route path={PAGE_ACCESS_RULES.liveAdjustHubPage.path} element={
                          <AccessGuard {...toAccessGuardProps(PAGE_ACCESS_RULES.liveAdjustHubPage)}>
                            <LiveAdjustHub />
                          </AccessGuard>
                        } />
                        <Route path={PAGE_ACCESS_RULES.matchDetailsPage.path} element={
                          <AccessGuard {...toAccessGuardProps(PAGE_ACCESS_RULES.matchDetailsPage)}>
                            <MatchDetailsPage />
                          </AccessGuard>
                        } />
                        <Route path={PAGE_ACCESS_RULES.scheduleMatchPage.path} element={
                          <AccessGuard {...toAccessGuardProps(PAGE_ACCESS_RULES.scheduleMatchPage)}>
                            <ScheduleMatchPage />
                          </AccessGuard>
                        } />
                        <Route path={PAGE_ACCESS_RULES.recordPage.path} element={
                          <RecordRouteGuard>
                            <RecordGamePage />
                          </RecordRouteGuard>
                        } />
                        <Route path={PAGE_ACCESS_RULES.manageUserPage.path} element={<ManageUserPage />} />
                        <Route path={PAGE_ACCESS_RULES.manageBansPage.path} element={<ManageBansPage />} />
                        <Route path={PAGE_ACCESS_RULES.managePlayersPage.path} element={<ManagePlayersPage />} />
                        <Route path={PAGE_ACCESS_RULES.pointsManagePage.path} element={<PointsManagePage />} />
                        <Route path={PAGE_ACCESS_RULES.drawPage.path} element={
                          <AccessGuard {...toAccessGuardProps(PAGE_ACCESS_RULES.drawPage)}>
                            <DrawPage />
                          </AccessGuard>
                        } />
                        <Route path={PAGE_ACCESS_RULES.drawAnySourcePage.path} element={
                          <AccessGuard {...toAccessGuardProps(PAGE_ACCESS_RULES.drawAnySourcePage)}>
                            <DrawAnySourcePage />
                          </AccessGuard>
                        } />
                        <Route path={PAGE_ACCESS_RULES.drawDistributionPage.path} element={
                          <AccessGuard {...toAccessGuardProps(PAGE_ACCESS_RULES.drawDistributionPage)}>
                            <DrawDistributionPage />
                          </AccessGuard>
                        } />
                        {/* 淘汰赛 */}
                        <Route path={PAGE_ACCESS_RULES.knockoutPage.path} element={
                          <AccessGuard {...toAccessGuardProps(PAGE_ACCESS_RULES.knockoutPage)}>
                            <KnockoutPage />
                          </AccessGuard>
                        } />
                        {/* 小组赛概览 */}
                        <Route path={PAGE_ACCESS_RULES.groupOverviewPage.path} element={
                          <AccessGuard {...toAccessGuardProps(PAGE_ACCESS_RULES.groupOverviewPage)}>
                            <GroupOverviewPage />
                          </AccessGuard>
                        } />
                        {/* 现场实时比分 */}
                        <Route path={PAGE_ACCESS_RULES.nowPlayingPageV2.path} element={
                          <AccessGuard {...toAccessGuardProps(PAGE_ACCESS_RULES.nowPlayingPageV2)}>
                            <NowPlayingPageV2 />
                          </AccessGuard>
                        } />
                        <Route path={PAGE_ACCESS_RULES.nowPlayingPageForOne.path} element={
                          <AccessGuard {...toAccessGuardProps(PAGE_ACCESS_RULES.nowPlayingPageForOne)}>
                            <NowPlayingPageForOne />
                          </AccessGuard>
                        } />
                        <Route path={PAGE_ACCESS_RULES.nowPlayingPageForStreaming.path} element={
                          <AccessGuard {...toAccessGuardProps(PAGE_ACCESS_RULES.nowPlayingPageForStreaming)}>
                            <NowPlayingPageForStreaming />
                          </AccessGuard>
                        } />
                        {/* 现场编排 */}
                        <Route path={PAGE_ACCESS_RULES.liveReorderPage.path} element={
                          <AccessGuard {...toAccessGuardProps(PAGE_ACCESS_RULES.liveReorderPage)}>
                            <LiveReorderPage />
                          </AccessGuard>
                        } />
                    </Routes>
                </Suspense>
            </Router>
        </FooterNavContext.Provider>
    );
}

export default App;
